<template>
  <DialogRoot :open="modelValue" @update:open="handleOpenChange">
    <DialogPortal>
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div v-if="modelValue" class="fixed inset-0 z-50">
          <!-- Backdrop -->
          <DialogOverlay class="fixed inset-0" :class="overlayClass" />

          <!-- Panel container -->
          <div
            class="fixed inset-0 flex justify-center overflow-y-auto p-4"
            :class="containerClass"
          >
            <!-- Panel -->
            <DialogContent
              :class="[
                'relative w-full bg-white dark:bg-[#1b2022] focus:outline-none',
                fullscreen
                  ? ['h-screen', ui?.fullscreen]
                  : [
                      'max-w-lg my-4 rounded-lg shadow-xl',
                      ui?.rounded,
                      ui?.shadow,
                    ],
                ui?.background,
                ui?.base,
              ]"
              @pointer-down-outside="handleOutsideInteraction"
              @escape-key-down="handleEscapeKey"
            >
              <slot />
            </DialogContent>
          </div>
        </div>
      </Transition>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup lang="ts">
  import {
    DialogContent,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
  } from "reka-ui"

  const props = defineProps({
    modelValue: {
      type: Boolean,
      default: false,
    },
    preventClose: {
      type: Boolean,
      default: false,
    },
    fullscreen: {
      type: Boolean,
      default: false,
    },
    ui: {
      type: Object,
      default: () => ({}),
    },
  })

  const emit = defineEmits(["update:modelValue"])

  const containerClass = computed(
    () => props.ui?.container || "items-start md:items-center",
  )
  const overlayClass = computed(
    () => props.ui?.overlay?.background || "bg-black/60",
  )

  function handleOpenChange(open: boolean) {
    if (!open && !props.preventClose) {
      emit("update:modelValue", false)
    }
  }

  function handleOutsideInteraction(event: Event) {
    if (props.preventClose) {
      event.preventDefault()
    }
  }

  function handleEscapeKey(event: KeyboardEvent) {
    if (props.preventClose) {
      event.preventDefault()
    }
  }

  // Scroll lock
  watch(
    () => props.modelValue,
    (open) => {
      if (open) {
        document.body.style.overflow = "hidden"
      } else {
        document.body.style.overflow = ""
      }
    },
  )

  onUnmounted(() => {
    document.body.style.overflow = ""
  })
</script>
